import { CheckCircle2, KeyRound, Loader, Trash2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { transformUrl, useAuth } from "@/auth/auth";
import { useOrganization } from "@/client/backend/organization";
import { normalizeSecretList, useDeleteOrganizationSecret, useOrganizationSecrets } from "@/client/backend/secrets";
import { useGooglePlaySession } from "@/client/googlePlay";
import { AppShell, EmptyState, PageHeader } from "@/components/layout/AppShell";
import { AppleSigningDialog } from "@/components/secrets/AppleSigningDialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const SECRET_TYPE_LABELS: Record<string, string> = {
	androidSigningKey: "Android signing key",
	appleCertificate: "Apple certificate",
	appleProvisioningProfile: "Apple provisioning profile",
};

function typeLabel(secretType: string): string {
	return SECRET_TYPE_LABELS[secretType] ?? secretType;
}

export function Secrets() {
	const auth = useAuth();
	const [organization] = useOrganization();
	const organizationTid = organization !== "loading" ? organization.tid : undefined;
	const devRegistryApiUrl = import.meta.env.DEV
		? new URLSearchParams(window.location.search).get("registryApiUrl") || undefined
		: undefined;
	const configuredRegistryApiUrl = import.meta.env.VITE_REGISTRY_API_ENDPOINT as string | undefined;
	const registryApiUrl = devRegistryApiUrl ?? transformUrl(configuredRegistryApiUrl);
	const { data, error, mutate } = useOrganizationSecrets(organizationTid);
	const { trigger: deleteSecret } = useDeleteOrganizationSecret(organizationTid);
	const [deleting, setDeleting] = useState<string>();
	const [deleteError, setDeleteError] = useState<string>();
	const google = useGooglePlaySession();

	// Warm up the Google sign-in script so the consent popup opens inside
	// the click gesture and popup blockers allow it.
	useEffect(() => {
		if (google.configured) {
			void google.preload();
		}
	}, [google]);

	const secrets = useMemo(
		() =>
			[...(normalizeSecretList(data?.data) ?? [])].sort(
				(left, right) => new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime(),
			),
		[data],
	);
	const loaded = data?.data !== undefined;

	async function handleDelete(secretType: string, secretName: string) {
		if (!window.confirm(`Delete secret ${secretName}? Builds relying on it will stop working.`)) {
			return;
		}
		const key = `${secretType}/${secretName}`;
		setDeleting(key);
		setDeleteError(undefined);
		try {
			const response = await deleteSecret({ secretType, secretName });
			if (response?.error) {
				setDeleteError(response.error.message);
				return;
			}
			await mutate();
		} finally {
			setDeleting(undefined);
		}
	}

	return (
		<AppShell title="Secrets">

			<PageHeader title="Secrets" description="Manage the signing credentials used by your builds." actions={<div className="flex flex-wrap items-center justify-end gap-2">
				<Button
					variant="outline"
					onClick={() => void google.signIn()}
					disabled={google.signingIn || !google.configured}
					title={
						google.configured
							? "Signed in for this browser session only; nothing is stored."
							: "Google sign-in is not configured for this console deployment."
					}
				>
					{google.signingIn ? (
						<>
							<Loader className="mr-2 h-4 w-4 animate-spin" />
							Connecting...
						</>
					) : google.isSignedIn ? (
						<>
							<CheckCircle2 className="mr-2 h-4 w-4 text-success" />
							Play Console connected
						</>
					) : (
						"Connect Play Console"
					)}
				</Button>
				<AppleSigningDialog
					registryApiUrl={registryApiUrl}
					registryToken={auth.token ?? undefined}
					organizationId={organizationTid}
					onSecretsSaved={() => void mutate()}
				/>
			</div>} />

			{error && (
				<div role="alert" className="rounded-lg border border-destructive/30 bg-destructive/10 p-4 text-sm text-danger">
					Failed to load secrets: {error.message}
				</div>
			)}
			{data?.error && (
				<div role="alert" className="rounded-lg border border-destructive/30 bg-destructive/10 p-4 text-sm text-danger">
					Failed to load secrets: {data.error.message}
				</div>
			)}
			{deleteError && (
				<div role="alert" className="rounded-lg border border-destructive/30 bg-destructive/10 p-4 text-sm text-danger">
					Failed to delete secret: {deleteError}
				</div>
			)}
			{google.error && (
				<div role="alert" className="rounded-lg border border-destructive/30 bg-destructive/10 p-4 text-sm text-danger">
					Play Console sign-in failed: {google.error}
				</div>
			)}



			<div className="min-w-0 border-t">
				{error || data?.error ? null : !loaded ? (
					<div className="p-6 text-sm text-muted-foreground">Loading secrets...</div>
				) : secrets.length === 0 ? (
					<EmptyState icon={<KeyRound />} title="No secrets stored" description="Connect your Apple Developer account or run a Gradle build to store signing credentials here." />
				) : (
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead>Name</TableHead>
								<TableHead>Kind</TableHead>
								<TableHead>Created</TableHead>
								<TableHead className="w-12">
									<span className="sr-only">Actions</span>
								</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{secrets.map((secret) => {
								const key = `${secret.type}/${secret.name}`;
								return (
									<TableRow key={secret.id}>
										<TableCell className="max-w-96 truncate font-semibold" title={secret.name}>
											{secret.name}
										</TableCell>
										<TableCell>
											<Badge variant="outline">{typeLabel(secret.type)}</Badge>
										</TableCell>
										<TableCell className="text-muted-foreground">
											{new Date(secret.createdAt).toLocaleString()}
										</TableCell>
										<TableCell>
											<Button
												variant="ghost"
												size="icon"
												onClick={() => void handleDelete(secret.type, secret.name)}
												disabled={deleting === key}
											>
												<Trash2 className="h-4 w-4 text-danger" />
												<span className="sr-only">Delete {secret.name}</span>
											</Button>
										</TableCell>
									</TableRow>
								);
							})}
						</TableBody>
					</Table>
				)}
			</div>
		</AppShell>
	);
}
